import { Component, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { City } from 'src/app/core/interfaces/city';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { CityService } from '../city.service';
import { Utils } from 'src/app/core/resources/utils';
import { AutocompleteCityComponent } from 'src/app/shared/autocomplete-city/autocomplete-city.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/shared/dialog/dialog.component';
import { IcaFormComponent } from '../ica-form/ica-form.component';
import { FormMessages } from 'src/app/core/messages/form-messages.enum';
import { Permission } from 'src/app/core/resources/permission';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { AuthService } from 'src/app/core/services/authentication.service';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { IntegrationEnum } from 'src/app/core/enums/integration.enum';
import { Subscription } from 'rxjs';
import { Tax } from 'src/app/core/interfaces/tax';
import { ApiResponseSiigo, ProductSiigo } from 'src/app/core/interfaces/productSiigo';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { OnboardingTourService } from 'src/app/core/services/onboarding-tour.service';
import { DriveStep } from 'driver.js';
import { CatalogService } from 'src/app/core/services/catalog.service';

@Component({
  selector: 'app-city-form',
  templateUrl: './city-form.component.html',
  styleUrls: ['./city-form.component.scss']
})
export class CityFormComponent implements OnInit {

  @ViewChild(AutocompleteCityComponent) autocompleteCityComponent: AutocompleteCityComponent;
  @ViewChild('mySelect') mySelect: MatSelect;
  permission = Permission;
  cityForm = new FormGroup({
    id: new FormControl('', Validators.required),
    name: new FormControl('', Validators.required),
    reteica: new FormControl('', [Validators.required, Validators.min(0.01), Validators.max(1)]),
    taxSiigo: new FormControl('', Validators.required),
    withholdingTaxPercentage: new FormControl(''),
    productSiigo: new FormControl('', Validators.required),
    icaAccount: new FormControl(''),
    icaSiigo: new FormControl('', Validators.required),
    debitAccount: new FormControl('', Validators.required),
    zoneCode: new FormControl('', Validators.required),
  });
  mainCityName = new FormControl({ value: '', disabled: true });
  taxSiigoControl = new FormControl('', Validators.required);
  city: City;
  subcities: any = [];
  // Elegidas en "Añadir Subciudad" y aún sin guardar: se muestran junto al botón, no en el listado con scroll.
  pendingSubcities: City[] = [];
  reteicaCity: any;
  minTaxSiigo: number = 1;
  minIcaSiigo: number = 1;
  optionsSubcity: OptionsAutocomplete = {
    title: 'Buscar ciudad para agregar',
    appearance: 'outline',
    requireFullCity: true
  };
  taxOptions: Tax[] = [];
  siigoOptions: ProductSiigo[];
  subCityControl: FormControl = new FormControl();
  subCitySub: Subscription;
  subCityFilterControl: FormControl = new FormControl();
  enablePagingScroll = true;
  pageKey = 1;
  pageSize = 50;
  // Producto guardado en la ciudad que no venía en las páginas cargadas: se fija de primero para que el select lo muestre.
  private pinnedProductSiigo: ProductSiigo = null;
  private static readonly MAX_PAGES_TO_FIND_PRODUCT = 20;
  nitCompany: string = '';
  cityId: string = '';
  // Llega del listado por query param; la pastilla lo valida contra las empresas de la SaaS y se puede cambiar.
  initialTransportNit: string = null;
  transportNit: string = null;
  private transportCompanyLoaded: boolean = false;
  constructor(
    private router: Router,
    private snackBarService: SnackBarService,
    private spinner: NgxSpinnerService,
    private cityService: CityService,
    private route: ActivatedRoute,
    private _location: Location,
    private permissionRole: PermissionRole,
    public utils: Utils,
    public dialog: MatDialog,
    public authService: AuthService,
    public onboardingTourService: OnboardingTourService,
    private catalogService: CatalogService
  ) { }

  async ngOnInit() {
    this.initialTransportNit = this.route.snapshot.queryParams['transportCompanyId'] || null;
    this.cityId = this.route.snapshot.params['id'];
    // Opciones Siigo y ciudad dependen de la empresa de transporte: las carga onTransportCompanyChange.
    this.spinner.show();
    this.setSubscriptionCity();
  }

  private async loadOptions(isFirstLoad: boolean): Promise<void> {
    if (this.hasSiigoIntegration) {
      // Productos Siigo paginan con "Cargar más": al cambiar de empresa se empieza de nuevo.
      this.pageKey = 1;
      this.pinnedProductSiigo = null;
      await this.getOptions();
      if (isFirstLoad && history && history.state && history.state.byRedirect) {
        this.spinner.hide();
        this.startTour();
        this.nitCompany = history.state.nitCompany;
      }
    }
  }

  /**
   * La ciudad y sus catálogos Siigo (retefuente, productos) se consultan por
   * empresa de transporte. La pastilla arranca con la empresa del listado
   * (?transportCompanyId=) y se puede cambiar: se recarga todo con la nueva
   * empresa (se descarta lo que se estuviera editando) y queda como la última
   * elegida, para que el listado muestre la misma al volver. Las opciones van
   * primero porque initFormCity las usa para preseleccionar.
   */
  public async onTransportCompanyChange(transportNit: string): Promise<void> {
    const isFirstLoad = !this.transportCompanyLoaded;
    if (!isFirstLoad && transportNit === this.transportNit) return;
    this.transportCompanyLoaded = true;
    this.transportNit = transportNit;
    // Lo escrito en "Añadir Subciudad" y sus resultados eran de la empresa anterior.
    if (!isFirstLoad) {
      this.subCityControl.setValue('');
      this.pendingSubcities = [];
    }
    this.optionsSubcity.transportNit = transportNit;
    if (transportNit) this.cityService.lastTransportCompanyId = transportNit;
    this.syncTransportCompanyInUrl(transportNit);
    this.spinner.show();
    await this.loadOptions(isFirstLoad);
    await this.getCity();
    this.spinner.hide();
  }

  /** replaceUrl: cambiar de empresa no agrega pasos al historial del navegador. */
  private syncTransportCompanyInUrl(transportNit: string): void {
    if (!transportNit || this.route.snapshot.queryParams['transportCompanyId'] === transportNit) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { transportCompanyId: transportNit },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  startTour(): void {
    setTimeout(() => {
      let steps: DriveStep[] = [];
      steps.push(
        {
          element: '#taxSiigoStep',
          popover: {
            title: 'Código retefuente',
            description: 'Debes seleccionar el código retefuente correspondiente a la ciudad.',
            side: 'bottom'
          },
          disableActiveInteraction: true,
        },
        {
          element: '#productSiigoStep',
          popover: {
            title: 'Producto Siigo',
            description: 'Finalmente debes seleccionar el producto Siigo correspondiente a la ciudad.',
          },
          disableActiveInteraction: true
        }
      );

      if (!this.onboardingTourService.driverInstance) this.onboardingTourService.setDriverInstanceConfig();
      this.onboardingTourService.setSteps(steps);
      this.onboardingTourService.startTour();
    }, 800);
  }

  private async getOptions() {
    try {
      let response = await this.cityService.getTaxes(this.transportNit).toPromise();
      this.taxOptions = response && response.data ? response.data : [];
    } catch (e) {
      this.snackBarService.openSnackBar('Ocurrió un error al traer los impuestos', undefined, 'error');
      this.taxOptions = [];
    }
    await this.getProductSiigo();
  }

  async onProductSiigoChange(value: string) {
    if (value == "0") {
      this.siigoOptions.pop()
      this.pageKey++;
      this.spinner.show();
      await this.getProductSiigo();
      this.spinner.hide();
    }
  }

  /**
   * El select solo muestra el valor si su producto está entre las opciones, y estas paginan de a 50:
   * si el guardado no está en lo cargado, se busca en las páginas siguientes (sin mover "Cargar más")
   * y se fija de primero. Si no aparece, se agrega con su código para que el campo no quede vacío.
   */
  private async ensureSelectedProductSiigoOption(): Promise<void> {
    const code = this.cityForm && this.cityForm.get('productSiigo') ? this.cityForm.get('productSiigo').value : null;
    if (!this.hasSiigoIntegration || !code) return;
    if ((this.siigoOptions || []).some(product => product.code === code)) return;
    const requestedTransportNit = this.transportNit;
    let found: ProductSiigo = null;
    try {
      for (let page = 2; page <= CityFormComponent.MAX_PAGES_TO_FIND_PRODUCT && !found; page++) {
        const response = await this.cityService.getProductSiigo(page, this.pageSize, requestedTransportNit).toPromise();
        const results = response && response.results ? response.results : [];
        found = results.find(product => product.code === code) || null;
        if (results.length < this.pageSize) break;
      }
    } catch (error) { }
    // Cambió la empresa o el valor mientras se buscaba: manda la carga más reciente.
    if (requestedTransportNit !== this.transportNit || this.cityForm.get('productSiigo').value !== code) return;
    this.pinnedProductSiigo = found || { code, name: code, active: true, type: '', taxes: [] };
    this.siigoOptions = [this.pinnedProductSiigo, ...(this.siigoOptions || []).filter(product => product.code !== code)];
  }

  onTaxSiigoChange($event: MatSelectChange) {
    const value: Tax = $event.value;
    this.cityForm.get('taxSiigo').setValue(value.id);
    this.cityForm.get('withholdingTaxPercentage').setValue(value.percentage);
  }

  public async getProductSiigo() {
    try {
      const response = await this.cityService.getProductSiigo(this.pageKey, this.pageSize, this.transportNit).toPromise();
      const results = response && response.results ? response.results : [];
      const pinned = this.pinnedProductSiigo;
      const pageResults = pinned ? results.filter(product => product.code !== pinned.code) : results;
      if (this.pageKey && this.pageKey == 1) { this.siigoOptions = pinned ? [pinned, ...pageResults] : pageResults; }
      else {
        this.siigoOptions = [...this.siigoOptions, ...pageResults];
        this.mySelect.open();
      }
      if (results.length == 50) {
        this.siigoOptions.push({
          code: "0",
          active: true,
          taxes: [],
          type: "",
          name: "Cargar más"
        })
      }
    } catch (error) {
      this.snackBarService.openSnackBar('Ocurrió un error al traer los productos siigo', undefined, 'error');
      this.siigoOptions = [];
    }
  }

  public async getCity(refresh: boolean = false) {
    const requestedTransportNit = this.transportNit;
    let city: City = await this.getCityById(this.cityId);
    if (!city) city = await this.findCityById(this.cityId);
    // Se cambió de empresa mientras se consultaba: manda la carga más reciente.
    if (requestedTransportNit !== this.transportNit) return;
    if (!city) {
      this.spinner.hide();
      this.snackBarService.openSnackBar('Ciudad no encontrada', undefined, 'error');
      this.router.navigate(['/administration/cities']);
      return;
    }
    this.city = city;
    this.initFormCity(this.city);
    this.cityForm.disable();
    await this.getSubcity(this.city);
  }

  private async findCityById(cityId: string): Promise<City> {
    try {
      const cities = await this.catalogService.getCityByMunicipalityCode(cityId, 1, 1, this.transportNit).toPromise() as City[];
      return cities && cities.length ? cities.find(city => city && city.id === cityId) || null : null;
    } catch (error) {
      return null;
    }
  }

  public async getSubcity(city) {
    try {
      const response: City[] = await this.cityService.getSubcityByIdCity(city.id, this.transportNit).toPromise();
      this.subcities = response && response.length ? response : [];
      this.orderSubcities();
    } catch (error) {
      this.snackBarService.openSnackBar('Ocurrió un error al traer las subciudades', undefined, 'error');
      this.subcities = [];
    }
  }

  private orderSubcities() {
    if (this.subcities && this.subcities.length)
      this.subcities = this.subcities.sort((a: City, b: City) => a.name.localeCompare(b.name));
  }

  public get subcitiesToShow(): City[] {
    if (!this.subcities || !this.subcities.length) return [];
    if (!this.subCityFilterControl.value) return this.subcities;
    return this.subcities.filter(subcity => subcity.name.toLowerCase().includes(this.subCityFilterControl.value.toLowerCase()));
  }

  public initFormCity(city: City) {
    this.reteicaCity = this.cityService.getReteicaCity(this.city);
    this.cityForm = new FormGroup({
      id: new FormControl(city.id || ""),
      name: new FormControl(city.name || "", Validators.required),
      reteica: new FormControl(this.cityService.getReteicaCity(this.city) || 0, [Validators.required]),
      taxSiigo: new FormControl(this.cityService.getCityValue(this.city, "taxSiigo") || 0, this.fieldsEnabledForTeclogi('taxSiigo') ? [Validators.required, Validators.min(this.minTaxSiigo)] : null),
      withholdingTaxPercentage: new FormControl(this.cityService.getCityValue(this.city, "withholdingTaxPercentage") || 0),
      productSiigo: new FormControl(this.cityService.getCityValue(this.city, "productSiigo") || "", this.fieldsEnabledForTeclogi('productSiigo') ? Validators.required : null),
      icaAccount: new FormControl(this.cityService.getCityValue(this.city, "icaAccount") || ""),
      icaSiigo: new FormControl(this.cityService.getCityValue(this.city, "icaSiigo") || 0, this.fieldsEnabledForTeclogi('icaSiigo') ? [Validators.required, Validators.min(this.minIcaSiigo)] : null),
      debitAccount: new FormControl(this.cityService.getCityValue(this.city, "debitAccount") || ""),
      zoneCode: new FormControl(this.cityService.getCityValue(this.city, "zoneCode") || "", this.fieldsEnabledForTeclogi('zoneCode') ? Validators.required : null),
    })
    this.mainCityName = new FormControl({ value: city.mainCity ? city.mainCity.name : '', disabled: true });

    // Se limpia siempre: si la ciudad no tiene retefuente (o no existe en las opciones de esta empresa),
    // no debe quedar la selección previa al cancelar la edición o al cambiar de empresa de transporte.
    const cityTaxSiigo = this.cityService.getCityValue(this.city, 'taxSiigo');
    const currentTax = cityTaxSiigo ? this.taxOptions.find(tax => tax && tax.id === cityTaxSiigo) : null;
    this.taxSiigoControl.reset(currentTax || '');


    this.ensureSelectedProductSiigoOption();
  }

  private fieldsEnabledForTeclogi(field: string): boolean {
    if (this.authService && this.authService.getCompany().companyId == '9013312320' && this.hasSiigoIntegration) {
      return ['name', 'reteica', 'taxSiigo', 'productSiigo', 'icaSiigo'].includes(field);
    } else if (this.hasSiigoIntegration) {
      return ['name', 'reteica', 'taxSiigo', 'productSiigo'].includes(field);
    } else if (this.hasWorldOfficeIntegration) {
      return ['zoneCode'].includes(field);
    }
    return false;
  }

  public async getCityById(id: string): Promise<City> {
    try {
      const response: City = await this.cityService.getCityById(id, this.transportNit).toPromise();
      return response && response.id ? response : null;
    } catch (error) {
      return null;
    }
  }

  public editRteica() {
    if (this.isMainCity) return;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      city: this.city,
      transportNit: this.transportNit
    };
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.autoFocus = false;
    this.dialog.open(IcaFormComponent, dialogConfig).afterClosed()
      .subscribe((result) => {
        if (result && result.city) {
          this.city = result.city;
          this.initFormCity(this.city);
          this.cityForm.disable();
        }
      });

  }

  get isMainCity() {
    if (!this.city || (this.city && this.city.mainCity)) return false;
    if (this.subcities && this.subcities.length) return true;
    return (this.city.debitAccount && (!this.hasSiigoIntegration || (this.city.productSiigo && this.city.taxSiigo && this.city.icaSiigo)));
  }

  get isSubCity() {
    return this.city && this.city.mainCity;
  }

  get subcityCannotBeEditedMessage(): string {
    return ServiceMessages.SUBCITY_CANNOT_BE_EDITED;
  }

  public editForm() {
    if (this.isSubCity) {
      this.snackBarService.openSnackBar(ServiceMessages.SUBCITY_CANNOT_BE_EDITED, undefined, 'alert');
      return;
    }
    this.cityForm.enable();
    // El nombre de la ciudad no es editable: solo se habilitan los demás campos.
    this.cityForm.get('name').disable();
  }

  public async cancelEditForm() {
    this.spinner.show();
    await this.getCity();
    this.spinner.hide();
    this.cityForm.disable();
  }

  public saveCity() {
    this.cityForm.markAllAsTouched();
    if (this.utils.errorMessagesCustomized(this.cityForm.get('name'), 'nombre de la ciudad')) return;
    if (this.utils.errorMessagesCustomized(this.cityForm.get('reteica'), 'reteica', null, null, 0.01, 1)) return;
    const reteica = this.cityForm.get('reteica').value;
    if (reteica && typeof reteica === 'string' && reteica.includes(",")) this.cityForm.get('reteica').setValue(reteica.replace(",", "."));
    const realReteica = Number(reteica);
    if (realReteica < 0.01 || realReteica > 1)
      return this.snackBarService.openSnackBar('El valor de la reteica debe estar entre 0.01 y 1', undefined, 'alert');

    if (this.utils.errorMessagesCustomized(this.cityForm.get('taxSiigo'), 'código retefuente', null, null, this.minTaxSiigo)) return;
    if (this.utils.errorMessagesCustomized(this.cityForm.get('productSiigo'), 'producto siigo')) return;
    if (this.utils.errorMessagesCustomized(this.cityForm.get('icaAccount'), 'cuenta ica')) return;
    if (this.utils.errorMessagesCustomized(this.cityForm.get('icaSiigo'), 'código ica', null, null, this.minIcaSiigo)) return;
    if (this.utils.errorMessagesCustomized(this.cityForm.get('debitAccount'), 'cuenta débito')) return;
    if (this.utils.errorMessagesCustomized(this.cityForm.get('zoneCode'), 'código zonal')) return;
    if (this.cityForm.invalid) return this.snackBarService.openSnackBar(FormMessages.GENERAL_ERROR_DEFAULT, undefined, 'alert');

    // getRawValue: el nombre va deshabilitado y .value lo omitiría del body.
    let body = this.utils.clone(this.cityForm.getRawValue());
    body.reteica = realReteica;
    this.updateIndependentCity(body);
  }

  public updateSubcities(body) {
    this.spinner.show();
    this.cityService.updateMainCity(body, this.transportNit).subscribe(async (response: any) => {
      if (response.status == 200) {
        await this.getCity(true)
        this.snackBarService.openSnackBar(ServiceMessages.CITY_UPDATED);
        this.spinner.hide();
      } else {
        this.snackBarService.openSnackBar(ServiceMessages.ERROR_UPDATING_CITY, undefined, 'error');
        this.cancelEditForm();
      }
    }, async (err) => {
      if (err.status == 200) {
        await this.getCity(true)
        this.snackBarService.openSnackBar(ServiceMessages.CITY_UPDATED);
        this.spinner.hide();
      } else {
        this.snackBarService.openSnackBar(ServiceMessages.ERROR_UPDATING_CITY, undefined, 'error');
        this.cancelEditForm();
      }
    })
  }

  public updateIndependentCity(body, showSpinner = true) {
    showSpinner && this.spinner.show();
    this.cityService.updateIndependentCity(body, this.transportNit).toPromise()
      .then((success) => {
        if (success && success['id']) {
          this.spinner.hide();
          if (this.isMainCity && this.subcities) {
            this.updateSubcities(body);
          } else {
            this.getCity(true)
            this.cancelEditForm();
            this.snackBarService.openSnackBar(ServiceMessages.CITY_UPDATED);
          }
        } else {
          this.spinner.hide();
          this.snackBarService.openSnackBar(ServiceMessages.ERROR_UPDATING_CITY, undefined, 'error');
          this.cancelEditForm();
        }
      })
      .catch((error) => {
        this.spinner.hide();
        const errorMessage = error && typeof error.error === 'string' ? error.error : ServiceMessages.ERROR_UPDATING_CITY;
        this.snackBarService.openSnackBar(errorMessage, undefined, 'error');
        this.cancelEditForm();
      })
  }

  public onSubmit() {
    if (this.pendingSubcities && this.pendingSubcities.length) {
      // Se envían las ya asociadas más las nuevas, igual que antes. Se limpian sobre
      // copias: las guardadas conservan mainCity, que deleteSubcity usa para desasociar en el back.
      const subCities = [...this.subcities, ...this.pendingSubcities].map((subcity) => {
        const cleanSubcity: any = { ...subcity };
        delete cleanSubcity.reteica;
        delete cleanSubcity.mainCity;
        return cleanSubcity;
      });
      const city = {
        mainCity: {
          id: this.cityForm.controls.id.value,
          name: this.cityForm.controls.name.value,
          reteica: this.cityForm.controls.reteica.value,
          taxSiigo: this.city.taxSiigo,
          withholdingTaxPercentage: this.city.withholdingTaxPercentage || 0,
          productSiigo: this.city.productSiigo
        },
        subCities
      };
      this.addSubcities(city);
    } else {
      this.snackBarService.openSnackBar('Por favor seleccione una subciudad', undefined, 'alert')
    }
  }

  public addSubcities(city) {
    this.spinner.show();
    this.cityService.addSubcities(city, this.transportNit).toPromise()
      .then(async (success) => {
        if (success) {
          this.pendingSubcities = [];
          await this.getCity(true)
          this.snackBarService.openSnackBar(ServiceMessages.CITIES_ASSOCIATED);
        } else this.snackBarService.openSnackBar(ServiceMessages.ERROR_ASSOCIATING_CITIES, undefined, 'error');
        this.spinner.hide();
      })
      .catch((error) => {
        const errorMessage = error && typeof error.error === 'string' ? error.error : ServiceMessages.ERROR_ASSOCIATING_CITIES;
        this.snackBarService.openSnackBar(errorMessage, undefined, 'error');
      })
      .finally(() => {
        this.spinner.hide();
      });
  }

  public removePendingSubcity(subcity: City) {
    this.pendingSubcities = this.pendingSubcities.filter(pending => pending.id !== subcity.id);
  }

  public deleteSubcity(subcity) {
    const dialogConfig = new MatDialogConfig();
    const title = '¿Deseas eliminar permanentemente esta subciudad: ' + subcity.name + " ?";
    dialogConfig.data = {
      title: title,
    };
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.width = ModalEnum.SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.autoFocus = false;
    const dialogRef = this.dialog.open(DialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.state) {
        if (subcity.mainCity) {
          this.removeSubcity(subcity);
        } else {
          const index = this.subcities.findIndex((element) => element.id === subcity.id);
          this.subcities.splice(index, 1);
        }
      }
    });
  }

  public removeSubcity(subcity) {
    this.spinner.show();
    this.cityService.removeSubCity(subcity.id, this.transportNit).subscribe(
      async (data: any) => {
        if (data && data.message && data.message.includes('Error')) {
          this.snackBarService.openSnackBar(data.message, undefined, 'error');
        } else {
          this.snackBarService.openSnackBar('Subciudad desasociada correctamente');
          const index = this.subcities.findIndex((element) => element.id === subcity.id);
          await this.getCity(true);
          this.subcities.splice(index, 1);
        }
        this.spinner.hide();
      },
      (error) => {
        this.spinner.hide();
        this.snackBarService.openSnackBar('Ocurrió un error al realizar el proceso', undefined, 'error');
      }
    );
  }

  public goToBack() {
    if (this.nitCompany) {
      this.router.navigate(['/administration/companies/detail', this.nitCompany], {
        state: { tab: 'Configuración administrativa' }
      });
    } else {
      this._location.back();
    }
  }

  setSubscriptionCity() {
    this.subCitySub = this.subCityControl.valueChanges
      .subscribe(value => {
        if (value && value.id) {
          this.autocompleteCityComponent.formControlCity.setValue("");
          const alreadyAdded = [...this.subcities, ...this.pendingSubcities].some(city => city.id == value.id);
          if (!alreadyAdded) {
            this.pendingSubcities.push(value);
          } else {
            this.snackBarService.openSnackBar(ServiceMessages.CITY_ALREADY_LINKED, undefined, 'alert');
          }
        }
      })
  }

  ngOnDestroy() {
    if (this.subCitySub) this.subCitySub.unsubscribe();
  }

  //GETTERS
  get hasSiigoIntegration(): boolean {
    return this.authService && this.authService.getCompany().integrationCredentials &&
      this.authService.getCompany().integrationCredentials.length &&
      this.authService.getCompany().integrationCredentials.some(integration => integration && integration.integrationChannel && integration.integrationChannel.toLowerCase() === IntegrationEnum.SIIGO.toLowerCase() && integration.state);
  }
  get hasWorldOfficeIntegration(): boolean {
    return this.authService && this.authService.getCompany().integrationCredentials &&
      this.authService.getCompany().integrationCredentials.length &&
      this.authService.getCompany().integrationCredentials.some(integration => integration && integration.integrationChannel && integration.integrationChannel.toLowerCase() === IntegrationEnum.WORLD_OFFICE.toLowerCase());
  }
  get canLinkCities(): boolean {
    return this.permissionRole.hasPermission(
      this.permission.administration.module,
      this.permission.administration.linkCities
    );
  }

  async onScrollDown() {
    if (this.enablePagingScroll) {
      this.spinner.show();
      this.pageKey++;
      await this.getOptions();
      this.spinner.hide();
    }
  }

  public ifFieldMandatory(field: string): boolean {
    const control = this.cityForm.get(field);
    if (!control || !control.validator) {
      return false;
    }

    const validators = control.validator({} as any); // Ejecuta el validador
    return validators && validators.required ? true : false;
  }
}
